import { useMutation, useQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { RELEASE_CREDENTIAL_LOCK } from "components/Encampy/api";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { CredentialFieldsDisplay } from "components/PortalCredential/CredentialFieldsDisplay";
import {
  getRequiredLepcIdTypes,
  getRequiredStateIdTypes,
} from "encamp-shared/src/facility/alternateId";
import { findAlternateId } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { GetTierIiReportQuery } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import pluralize from "pluralize";
import { useState } from "react";
import { GET_STEERS_CREDENTIALS_PAGE_DATA } from "routes/Staff/Credentials/SteersCredentials/api";
import { prettyPrintDateTimeMed } from "util/dates";

import { CredentialVerification } from "./CredentialVerification";
import { FieldDisplay } from "./shared/FieldComponents";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

const VerificationSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

type FacilityAlternateId = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["facility"]["facilityAlternateIds"]
>[number];

type PortalCredentialItem = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["facility"]["portalCredentials"]
>[number];

type CredentialFromFragment = NonNullable<PortalCredentialItem["credential"]>;

function portalTypeLabel(type: string | null | undefined): string {
  return `${prettyPrintEnumValue(type)} Portal`;
}

/**
 * LEPC `Portal.jurisdiction` is encoded as `${state}_${lepcId}`. Mirrors the
 * producer in `syncCredentialImplementationsForFacility`.
 */
function extractLepcIdFromJurisdiction(jurisdiction: string | null | undefined): string | null {
  if (!jurisdiction) return null;
  const underscoreIndex = jurisdiction.indexOf("_");
  return underscoreIndex >= 0 ? jurisdiction.slice(underscoreIndex + 1) : null;
}

export function PortalCredentials() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data } = useFulfillmentDetails();
  const [activeLockId, setActiveLockId] = useState<string | null>(null);
  const [releaseCredentialLock] = useMutation(RELEASE_CREDENTIAL_LOCK);

  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-state-portal-expanded",
    { defaultValue: true }
  );

  const facility = data?.tierIIReport.facility;
  const portalCredentialsList: PortalCredentialItem[] =
    facility && (facility.portalCredentials?.length ?? 0) > 0 ? facility.portalCredentials : [];
  const facilityAlternateIds = facility?.facilityAlternateIds;
  const reportingYear = data?.tierIIReport.reportingYear;
  const reportKind = data?.tierIIReport.reportKind;

  const isTexas = facility?.state === "TX";

  const verificationStatusOnly = portalCredentialsList.some(
    (pc) => pc?.credential?.verificationStatus
  ) ? (
    <Stack spacing={1}>
      {portalCredentialsList.map(
        (pc) =>
          pc?.credential && (
            <FieldDisplay
              key={pc.credential?.portal?.type ?? "portal"}
              label={`${portalTypeLabel(pc.credential?.portal?.type)} verification:`}
              value={prettyPrintEnumValue(pc.credential.verificationStatus)}
              noWrap
            />
          )
      )}
    </Stack>
  ) : null;

  const hasCredsToShow =
    (isTexas && (facilityAlternateIds?.length ?? 0) > 0) || portalCredentialsList.length > 0;

  const releaseButton = activeLockId ? (
    <Button
      size="small"
      color="warning"
      onClick={async () => {
        try {
          const { data: releaseData } = await releaseCredentialLock({
            variables: { lockId: activeLockId },
          });
          if (releaseData?.releaseCredentialLock) {
            setActiveLockId(null);
            alerts.success("Credential lock released");
          } else {
            alerts.error("Failed to release credential lock");
          }
        } catch {
          alerts.error("Failed to release credential lock");
        }
      }}
    >
      Release credential
    </Button>
  ) : null;

  if (!hasCredsToShow) {
    return null;
  }

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6" data-testid="state-portal-title">
          {pluralize("Credential", portalCredentialsList.length)}
        </Typography>
        <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      {!isExpanded && verificationStatusOnly && <Box sx={{ pl: 0 }}>{verificationStatusOnly}</Box>}

      <Collapse in={isExpanded}>
        <Grid container spacing={3}>
          <Grid size={12}>
            {isTexas ? (
              <TexasCredentialsContent
                facilityAlternateIds={facilityAlternateIds ?? []}
                portalCredentialsList={portalCredentialsList}
                reportingYear={reportingYear}
                reportKind={reportKind}
                onLockAcquired={(lockId) => setActiveLockId(lockId)}
                releaseButton={releaseButton}
              />
            ) : (
              <Stack spacing={3} divider={<Divider />}>
                {portalCredentialsList.map((pc, index) => {
                  const encampyDisabled = !pc?.credential?.username || !pc?.credential?.password;
                  return (
                    <Box key={pc?.credential?.portal?.type ?? index}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ pb: 1 }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          {portalTypeLabel(pc?.credential?.portal?.type)}
                        </Typography>
                        <Stack alignItems="end">
                          <EncampyPortalLoginLauncher
                            portalCredential={pc}
                            credentialId={pc?.credential?.id}
                            onLockAcquired={(lockId) => setActiveLockId(lockId)}
                            facilityAlternateIds={facilityAlternateIds}
                            reportingYear={reportingYear}
                            reportKind={reportKind}
                            onSuccess={(message) => alerts.success(message)}
                            onError={(error) => alerts.error(error)}
                            size="small"
                            disabled={encampyDisabled}
                            disabledReason={
                              encampyDisabled
                                ? "Portal credentials are required for portal login"
                                : undefined
                            }
                          />
                          {releaseButton}
                        </Stack>
                      </Stack>
                      <CredentialFieldsDisplay
                        credential={pc?.credential ?? undefined}
                        portalUrl={pc?.credential?.portal?.url ?? undefined}
                        showCombinedInvoicing={facility?.state === "WV"}
                      />
                      {pc?.credential && (
                        <VerificationSection sx={{ mt: 2 }}>
                          <CredentialVerification
                            credential={pc.credential}
                            facilityId={facility?.id ?? ""}
                            credentialVerificationAvailable={
                              pc?.credential?.portal?.verifiable ?? false
                            }
                          >
                            <CredentialLockIndicator credential={pc.credential} />
                            <FacilityInPortalStatus
                              credential={pc.credential}
                              facilityAlternateIds={facilityAlternateIds ?? []}
                              facilityState={facility?.state ?? null}
                            />
                          </CredentialVerification>
                        </VerificationSection>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Collapse>
    </Stack>
  );
}

/** For Texas, displays
 * - Error if the facility does not have a CN
 * - Number of associated credentials for a given STEERS CN
 * - Link to the STEERS Credentials page for the given CN
 * - "Open with Encampy" button (TX credentials are managed via STEERS, so no
 *   per-credential button is shown in the body like non-TX states)
 *  */
function TexasCredentialsContent({
  facilityAlternateIds,
  portalCredentialsList,
  reportingYear,
  reportKind,
  onLockAcquired,
  releaseButton,
}: {
  facilityAlternateIds: FacilityAlternateId[];
  portalCredentialsList: PortalCredentialItem[];
  reportingYear: number | undefined;
  reportKind: GetTierIiReportQuery["tierIIReport"]["reportKind"] | undefined;
  onLockAcquired: (lockId: string) => void;
  releaseButton: React.ReactNode;
}) {
  const alerts = useAlerts();
  const steersCn = findAlternateId(facilityAlternateIds, ["STEERS_CN"])?.value;

  const { data: steersData } = useQuery(GET_STEERS_CREDENTIALS_PAGE_DATA, {
    variables: { cn: steersCn },
    skip: !steersCn,
  });

  // When filtered by CN, there will be at most one mapping
  const cnMapping = steersData?.steersCredentialsPageData.mappings[0];
  const credentialCount = cnMapping?.credentialCount ?? 0;

  // Pick the first available credential for the Encampy launcher
  const portalCredential = portalCredentialsList[0];
  const encampyDisabled =
    !portalCredential?.credential?.username || !portalCredential?.credential?.password;

  if (!steersCn) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Warning color="warning" sx={{ fontSize: 18 }} />
        <Typography variant="body2" color="text.secondary">
          Facility must have a STEERS CN for credential associations
        </Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack alignItems="end" pb={1}>
        {portalCredential && (
          <EncampyPortalLoginLauncher
            portalCredential={portalCredential}
            steersCn={steersCn ?? undefined}
            onLockAcquired={onLockAcquired}
            facilityAlternateIds={facilityAlternateIds}
            reportingYear={reportingYear}
            reportKind={reportKind}
            onSuccess={(message) => alerts.success(message)}
            onError={(error) => alerts.error(error)}
            size="small"
            disabled={encampyDisabled}
            disabledReason={
              encampyDisabled ? "Portal credentials are required for portal login" : undefined
            }
          />
        )}
        {releaseButton}
      </Stack>
      <Link
        href={`/staff/credentials/steers?search=${encodeURIComponent(
          steersCn
        )}&associatedCredentials=true`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      >
        Manage Texas STEERS credential associations
        <OpenInNew sx={{ fontSize: 14 }} />
      </Link>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {credentialCount === 0 && <Warning color="warning" sx={{ fontSize: 14 }} />}
        <Typography
          variant="caption"
          color={credentialCount === 0 ? "warning.main" : "text.secondary"}
        >
          {credentialCount} credential{credentialCount !== 1 ? "s" : ""} associated with {steersCn}
        </Typography>
      </Stack>
    </Box>
  );
}

function CredentialLockIndicator({ credential }: { credential: CredentialFromFragment }) {
  const activeLock = credential.activeLock;
  if (!activeLock) return null;

  const holder = activeLock.holderEmail ?? activeLock.holderId;
  const expiry = prettyPrintDateTimeMed(activeLock.expiresAt);

  return (
    <FieldDisplay
      label="Currently In Use:"
      value={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LockOutlined color="info" sx={{ fontSize: 16 }} />
          <Typography variant="body2" color="info.main">
            {holder} (expires {expiry})
          </Typography>
        </Stack>
      }
    />
  );
}

function FacilityInPortalStatus({
  credential,
  facilityAlternateIds,
  facilityState,
}: {
  credential: CredentialFromFragment;
  facilityAlternateIds: FacilityAlternateId[];
  facilityState: string | null;
}) {
  if (credential.verificationStatus !== "GOOD") return null;

  const cpfEntries = credential.credentialPortalFacilities;
  if (!cpfEntries || cpfEntries.length === 0) return null;

  // Restrict matching to the alt id type(s) the portal actually uses, otherwise
  // an unrelated alt id (EPA, RCRAINFO, etc.) that happens to collide with
  // another credential's manifest value would produce a false positive.
  const portal = credential.portal;
  const expectedTypes =
    portal?.type === "LEPC"
      ? getRequiredLepcIdTypes(extractLepcIdFromJurisdiction(portal.jurisdiction))
      : getRequiredStateIdTypes(facilityState);

  const facilitySeenInPortal =
    expectedTypes.length > 0 &&
    facilityAlternateIds.some(
      (altId) =>
        expectedTypes.includes(altId.type) &&
        cpfEntries.some((cpf) => cpf.stateIdType === altId.type && cpf.stateIdValue === altId.value)
    );

  return (
    <FieldDisplay
      label={
        <Tooltip
          title="Indicates whether this facility's state ID was seen in the portal during the last credential verification check"
          placement="bottom"
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>Facility in Portal:</span>
            <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
          </Stack>
        </Tooltip>
      }
      value={
        facilitySeenInPortal ? (
          <Typography variant="body2" color="success.main">
            Yes
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Warning color="warning" sx={{ fontSize: 16 }} />
            <Typography variant="body2" color="warning.main">
              Not found
            </Typography>
          </Stack>
        )
      }
    />
  );
}
